import { OptionsObject } from "notistack";

export enum ReportingFrequency {
  MONTHLY = "Monthly",
  QUARTERLY = "Quarterly",
  SEMI_ANNUAL = "Semi-Annual",
  ANNUAL = "Annual",
}

export enum UnderlyingType {
  COVERED = "Covered",
  UNCOVERED = "Uncovered",
}

export enum ManagementType {
  DIRECT = "Direct",
  SOLICITATION = "Solicitation",
  SUB_ADVISOR = "Sub-Advisor",
}

export enum OrderStatus {
  DRAFT = "draft",
  NEW = "new",
  STAGED = "staged",
  OPEN = "open",
  REJECTED = "rejected",
  CANCELED = "canceled",
  ERROR = "error",
  PARTIALLY_FILLED = "partially_filled",
  FILLED = "filled",
}

export enum FormMode {
  CREATE,
  EDIT,
}

export enum StrategyGroupEnum {
  STANDARD = 1,
  DIV_OPTIC = 2,
  GROWTH_OPTIC = 3,
  HEDGE = 4,
}

export enum StrategyGroupLabel {
  "Standard" = StrategyGroupEnum.STANDARD,
  "Div Optic" = StrategyGroupEnum.DIV_OPTIC,
  "Growth Optic" = StrategyGroupEnum.GROWTH_OPTIC,
  "Hedge" = StrategyGroupEnum.HEDGE,
}

export const DEFAULT_POLLING_INTERVAL = 60_000;

export enum LocalStorageKeys {
  LAST_STRATEGY_ID = "lastStrategyId",
  LAST_STRATEGY_GROUP_ID = "lastStrategyGroupId",
  LAST_STATISTIC_STRATEGY_ID = "lastStatisticStrategyId",
  LAST_STATISTIC_ACCOUNT_ID = "lastStatisticAccountId",
  LAST_STATISTIC_DATE_RANGE = "lastStatisticDateRange",
  LAST_STATISTIC_PERIODS = "lastStatisticPeriods",
  LAST_CLIENT_STOCK_ID = `lastPerformanceStockClientId`,
  LAST_ACCOUNT_STOCK_IDS = `lastPerformanceStockAccountIds`,
  LAST_PERIODS_STOCK = `lastPerformanceStockPeriods`,
  LAST_CLIENT_STRATEGY_ID = `lastPerformanceStrategyClientId`,
  LAST_ACCOUNT_STRATEGY_IDS = `lastPerformanceStrategyAccountIds`,
  LAST_PERIODS_STRATEGY = `lastPerformanceStrategyPeriods`,
  LAST_REPORT_TAB = "lastReportTab",
}

export enum OrderType {
  MARKET = "market",
  LIMIT = "limit",
}

export const DEFAULT_LIMIT_PRICE = 0.0;

export const NOTISTACK_AUTO_HIDE_DURATION = 5_000;
export const NOTISTACK_MAX_SNACK = 3;

export const NOTISTACK_SUCCESS_OPTIONS: OptionsObject = {
  variant: "success",
  anchorOrigin: { horizontal: "right", vertical: "bottom" },
};
export const NOTISTACK_FAILURE_OPTIONS: OptionsObject = {
  variant: "error",
  anchorOrigin: { horizontal: "right", vertical: "bottom" },
};

// The login page anchors its failure banner top-right, where every other
// screen anchors bottom-right. It is the one page rendered without the Header,
// so the top-right corner is empty there rather than occupied by the app bar.
export const NOTISTACK_TOP_RIGHT_FAILURE_OPTIONS: OptionsObject = {
  variant: "error",
  anchorOrigin: { horizontal: "right", vertical: "top" },
  // maxSnack is 3, so without this a reader retrying inside the auto-hide
  // window stacks three copies of the same message.
  preventDuplicate: true,
};

export const NOTISTACK_ALERT_OPTIONS: OptionsObject = {
  variant: "info",
  anchorOrigin: { horizontal: "right", vertical: "bottom" },
  persist: true,
};

export enum FDS_ActionTypes {
  GET = "GET",
  SUBSCRIBE = "SUBSCRIBE",
  UNSUBSCRIBE = "UNSUBSCRIBE",
}

export enum FDS_SubscriptionTypes {
  HOMEPAGE_STOCK = "HOMEPAGE_STOCK",
  HOMEPAGE_OPTION = "HOMEPAGE_OPTION",
  OPTION_CONTRACT = "OPTION_CONTRACT",
  OPTION_CHAIN = "OPTION_CHAIN",
  STOCK_PRICE = "STOCK_PRICE",
}

export const VolDaysOptions = [
  {
    label: "1 Year",
    value: 360,
  },
  {
    label: "3 Years",
    value: 1080,
  },
  {
    label: "5 Years",
    value: 1800,
  },
];

export enum ReportingMode {
  ACCOUNT_PREVIEW,
  ALL_ACCOUNTS,
}

export enum ReportJobStatus {
  PENDING = "PENDING",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  NOT_FOUND = "NOT_FOUND",
}

export const CALLS_HISTORY_PERIODS = [30, 60, 90, 180, 360];

export enum DateSelectionMode {
  YEAR = "year",
  MONTH = "month",
  RANGE = "range",
  INCEPTION = "inception",
  BUCKET_INCEPTION = "bucket_inception",
}

export enum UrlPaths {
  REPORT_DEPRECATED = "/report-deprecated",
  REPORT = "/report",
  SETTINGS = "/settings",
  SECURITIES = "/securities",
}

// The side menu's collapsible group of retired pages. Deliberately one word:
// with the archive icon and the expand chevron placed, the row leaves 87px for
// its label in the 176px drawer, and "Obsolete Pages" needs 112px at 14px
// Poppins — it ellipsised. The four child rows supply the missing "pages".
export const OBSOLETE_PAGES_NAV_LABEL = "Obsolete";

export const OBSOLETE_PAGE_DISCLAIMER =
  "This page is no longer supported and may thus malfunction or display inaccurate data";

export enum SingleValueCardType {
  CURRENCY = "currency",
  PERCENTAGE = "percentage",
  NUMBER = "number",
}

export enum SingleValueCardColor {
  PRIMARY = "primary",
  SECONDARY = "secondary",
  SUCCESS = "success",
  ERROR = "error",
  WARNING = "warning",
  INFO = "info",
}

export enum ReportQuarterTableColumn {
  Period = "Period",
  StockPrice = "Stock Price",
  OptionIncome = "Option Income",
  DividenIncome = "Dividen Income",
  TotalIncome = "Total Income",
  Comment = "Comment",
}

export interface QuarterRow {
  id: number;
  [ReportQuarterTableColumn.Period]: string;
  [ReportQuarterTableColumn.StockPrice]: number;
  [ReportQuarterTableColumn.OptionIncome]: number;
  [ReportQuarterTableColumn.DividenIncome]: number;
  [ReportQuarterTableColumn.TotalIncome]: number;
  [ReportQuarterTableColumn.Comment]: string;
}

export enum BucketSummaryEnum {
  BUCKET_1 = "1",
  BUCKET_2 = "2",
  BUCKET_3 = "3",
  LONG_VALUE = "long_value",
  NET_VALUE = "net_value",
}

// "Long Value" is the buckets' sum, "Net Value" that sum with margin applied.
export const BUCKET_SUMMARY_LABELS: Record<BucketSummaryEnum, string> = {
  [BucketSummaryEnum.BUCKET_1]: "Bucket 1",
  [BucketSummaryEnum.BUCKET_2]: "Bucket 2",
  [BucketSummaryEnum.BUCKET_3]: "Bucket 3",
  [BucketSummaryEnum.LONG_VALUE]: "Long Value",
  [BucketSummaryEnum.NET_VALUE]: "Net Value",
};

// Which time-weighted return the bucket report calculates. Both are served
// by one engine (backend services/bucket_performance.py), so the two figures
// are comparable rather than two codebases' answers. DAILY_WEIGHT_ADJUSTMENT
// is the default; the pick is per session from the Summary section's gear and
// is never persisted.
//
// A third, "weights_as_of", stood here until task 3603 retired it with the
// arithmetic behind it. The backend coerces that stored string to the default
// rather than raising, so an older selection simply reads as Daily Weight
// Adjustment.
export enum BucketTwrFormula {
  DAILY_WEIGHT_ADJUSTMENT = "daily_weight_adjustment",
  BUCKET_MARKET_VALUE = "bucket_market_value",
}
